import {
	FormLabel,
	Skeleton,
	Typography,
	Stack,
	Box,
	Checkbox,
	Chip,
	IconButton,
	CircularProgress,
	Tooltip,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import React, { useEffect, useMemo, useState, createContext, useContext, useRef } from 'react';
import { FolderOutlined } from '@mui/icons-material';
import { getSharepointError, isFileExtensionAllowed } from '@/features/msGraph/helper';
import { useGetDriveItemQuery, useGetFoldersAndFilesQuery } from '@/features/msGraph/msGraphApi';
import { useTreeViewApiRef } from '@mui/x-tree-view';
import RefreshIcon from '@mui/icons-material/Refresh';

const FileSelectContext = createContext(null);

export const useFileSelectContext = () => {
	const context = useContext(FileSelectContext);
	if (!context) {
		throw new Error('useFileSelectContext must be used within FileSelectContextProvider');
	}
	return context;
};

export const FileSelectContextProvider = ({
	selectedFiles,
	setSelectedFiles,
	itemId,
	driveId,
	disableParent,
	multiple = true,
	types,
	autoExpandRoot = true,
}) => {
	const apiRef = useTreeViewApiRef();
	const [expandedItems, setExpandedItems] = useState(autoExpandRoot && itemId ? [itemId] : []);
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	const handleGlobalRefresh = () => {
		setRefreshTrigger((prev) => prev + 1);
	};

	const contextValue = {
		apiRef,
		selectedFiles,
		setSelectedFiles,
		expandedItems,
		setExpandedItems,
		itemId,
		driveId,
		disableParent,
		multiple,
		types,
		refreshTrigger,
		handleGlobalRefresh,
	};

	return (
		<FileSelectContext.Provider value={contextValue}>
			<SharepointFileTree />
		</FileSelectContext.Provider>
	);
};
const SharepointFileTreeItem = ({ item, disabled, index }) => {
	const {
		apiRef,
		expandedItems,
		selectedFiles,
		setSelectedFiles,
		multiple,
		types,
		refreshTrigger,
	} = useFileSelectContext();

	const expanded = useMemo(() => expandedItems.includes(item.id), [expandedItems, item.id]);
	const isFile = !!item.file || (!item.folder && item.size !== undefined);
	const isFolder = !!item.folder || (!isFile && item.children !== undefined);

	const folderQuery = useGetFoldersAndFilesQuery(
		{
			itemId: item?.id,
			driveId: item?.parentReference?.driveId,
		},
		{
			skip: !isFolder || !item.id || !item?.parentReference?.driveId || !expanded,
			refetchOnMount: false,
			refetchOnReconnect: false,
			refetchOnWindowFocus: false,
		},
	);

	const lastRefreshRef = useRef(0);
	const isInitialMount = useRef(true);
	const refetchRef = useRef(folderQuery.refetch);

	useEffect(() => {
		refetchRef.current = folderQuery.refetch;
	}, [folderQuery.refetch]);

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		if (isFolder && expanded && refetchRef.current && refreshTrigger > 0) {
			const now = Date.now();
			if (now - lastRefreshRef.current > 500) {
				lastRefreshRef.current = now;
				refetchRef.current();
			}
		}
	}, [refreshTrigger, isFolder, expanded]);

	const children = useMemo(() => {
		if (!isFolder || folderQuery.isLoading) return [];

		const { folders = [], files = [] } = folderQuery.data ?? {};

		const folderItems = folders.map((f) => ({
			...f,
			isFolder: true,
			isFile: false,
		}));

		const fileItems = files.map((f) => ({
			...f,
			isFolder: false,
			isFile: true,
		}));

		return [...folderItems, ...fileItems].sort((a, b) => {
			if (a.isFolder && !b.isFolder) return -1;
			if (!a.isFolder && b.isFolder) return 1;
			return (a.name || '').localeCompare(b.name || '');
		});
	}, [folderQuery.data, folderQuery.isLoading, isFolder]);

	const generateLoadingSkeletons = () =>
		Array.from({ length: Math.min(item?.folder?.childCount ?? 5, 10) }, (_, i) => (
			<Skeleton key={`loading-${item.id}-${i}`} sx={{ ml: 4, my: 0.5 }} />
		));

	const isSelected = useMemo(
		() => selectedFiles?.some((f) => f.id === item.id) || false,
		[selectedFiles, item.id],
	);

	const disabledDueToType = useMemo(
		() => isFile && !isFileExtensionAllowed(item?.name, types),
		[isFile, item?.name, types],
	);

	const isDisabled = useMemo(
		() => disabled || isFolder || disabledDueToType,
		[disabledDueToType, isFolder, disabled],
	);

	const canSelectMore = multiple ? true : !isSelected;

	const handleFileSelect = (e) => {
		e.stopPropagation();
		if (!isFile || isDisabled || !canSelectMore) return;
		if (multiple) {
			if (isSelected) {
				setSelectedFiles(selectedFiles.filter((f) => f.id !== item.id));
			} else {
				setSelectedFiles([
					...selectedFiles,
					{
						id: item.id,
						name: item.name,
						size: item.size,
						webUrl: item.webUrl,
						mimeType: item.file?.mimeType,
						parentReference: item.parentReference,
						driveId: item.parentReference.driveId,
					},
				]);
			}
		} else {
			if (isSelected) {
				setSelectedFiles([]);
			} else {
				setSelectedFiles([
					{
						id: item.id,
						name: item.name,
						size: item.size,
						webUrl: item.webUrl,
						mimeType: item.file?.mimeType,
						parentReference: item.parentReference,
						driveId: item.parentReference.driveId,
					},
				]);
			}
		}
		apiRef?.current?.focusItem(e, isSelected ? null : item.id);
	};

	const itemName = () => {
		if ((item.name === 'root' || item.root) && item.webUrl) {
			const url = item.webUrl.split('/');
			try {
				return decodeURI(url[url.length - 1].normalize());
			} catch (e) {
				return url[url.length - 1];
			}
		}
		return item.name;
	};

	return (
		<TreeItem
			itemId={item.id}
			label={
				<Box>
					<Tooltip title={disabledDueToType ? 'File type not allowed' : ''}>
						<Stack
							direction='row'
							alignItems='center'
							sx={{
								cursor:
									isFile && !isDisabled && canSelectMore ? 'pointer' : 'default',
							}}
							{...(isFile && !isDisabled && { onClick: handleFileSelect })}
						>
							{isFile ? (
								<Checkbox
									size='small'
									checked={isSelected}
									onChange={handleFileSelect}
									disabled={isDisabled || !canSelectMore}
									icon={<InsertDriveFileOutlined />}
									checkedIcon={<InsertDriveFileIcon />}
									sx={{ p: '4px' }}
								/>
							) : (
								<Box sx={{ width: 32, display: 'flex', justifyContent: 'center' }}>
									<FolderOutlined sx={{ fontSize: 20 }} />
								</Box>
							)}

							<Typography
								variant='body2'
								sx={{
									fontWeight: isSelected ? 'bold' : null,
									flex: 1,
									ml: isFile ? 0 : 0.5,
								}}
							>
								{itemName()}
							</Typography>
						</Stack>
					</Tooltip>
				</Box>
			}
		>
			{isFolder && (
				<>
					{folderQuery.isLoading ? (
						generateLoadingSkeletons()
					) : folderQuery.isError ? (
						<Box>
							<Typography variant='caption' color='error'>
								{getSharepointError(folderQuery.error)?.errorMessage}
							</Typography>
						</Box>
					) : children.length > 0 ? (
						children.map((child, i) => (
							<SharepointFileTreeItem
								key={`tree-item-${item.id}-${child.id || i}`}
								item={child}
								index={index + 1}
							/>
						))
					) : (
						<Box pl='3em'>
							<Typography variant='caption' color='text.secondary'>
								No items found
							</Typography>
						</Box>
					)}
				</>
			)}
		</TreeItem>
	);
};

const SharepointFileTree = () => {
	const { expandedItems, setExpandedItems, itemId, driveId, disableParent, handleGlobalRefresh } =
		useFileSelectContext();

	const {
		data: driveItem,
		isLoading,
		error,
		refetch: refetchRootItem,
	} = useGetDriveItemQuery(
		{
			driveId,
			itemId,
		},
		{ skip: !itemId || !driveId },
	);

	const [isRefreshing, setIsRefreshing] = useState(false);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		await refetchRootItem();
		handleGlobalRefresh();
		setIsRefreshing(false);
		setExpandedItems([itemId]);
	};

	const handleExpandedItemsChange = (event, itemIds) => {
		setExpandedItems(itemIds);
	};

	return (
		<Stack>
			<Stack direction='row' alignItems='center' spacing={0.5} sx={{ mb: 1 }}>
				<FormLabel>Browse files</FormLabel>
				<IconButton
					size='small'
					onClick={handleRefresh}
					disabled={isLoading || isRefreshing}
				>
					{isRefreshing ? (
						<CircularProgress size={16} sx={(theme) => ({ color: theme.palette.primary.main })} />
					) : (
						<RefreshIcon sx={(theme) => ({ color: theme.palette.primary.main })} fontSize='small' />
					)}
				</IconButton>
			</Stack>
			<Box
				sx={{
					maxHeight: '20em',
					overflowY: 'auto',
					pt: '1em',
					px: 1,
				}}
			>
				<SimpleTreeView
					disableSelection
					expandedItems={expandedItems}
					onExpandedItemsChange={handleExpandedItemsChange}
				>
					{isLoading ? (
						<Skeleton />
					) : driveItem ? (
						<SharepointFileTreeItem
							item={driveItem}
							disabled={disableParent}
							index={0}
						/>
					) : (
						<Typography variant='caption' color='error'>
							{getSharepointError(error)?.errorMessage}
						</Typography>
					)}
				</SimpleTreeView>
			</Box>
		</Stack>
	);
};

export default SharepointFileTree;
